import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import PageLoader from '../ui/PageLoader';
import EmptyState from '../ui/EmptyState';
import Button from '../ui/Button';
import { PageHeader } from '../ui/PageHeader';
import PageShell from '../ui/PageShell';
import PageBackLink from '../ui/PageBackLink';
import { getPassportFlags, getPassportRecap, isPassportEnabled } from '../../services/api/passport';
import { PASSPORT_DISABLED_DESCRIPTION, PASSPORT_DISABLED_TITLE } from './passportCopy';
import apiService from '../../services/ApiService';

type Recap = {
  year: number;
  tone?: { headline?: string; subhead?: string };
  summary?: Record<string, number>;
  favorites?: Record<string, { label?: string; count?: number } | null>;
  highlights?: Array<{ public_id: string; title?: string; verification_level?: string; occurred_at?: string }>;
  badges?: Array<{ name?: string; public_id?: string }>;
  memorable_build?: { title?: string } | null;
  rarest_encounter?: { title?: string; verification_level?: string } | null;
  share?: { path?: string; export_title?: string };
};

const PassportRecapPage: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const year = Number(params.get('year') || new Date().getFullYear());
  const [recap, setRecap] = useState<Recap | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/passport/recap?year=${year}` }, replace: true });
    }
  }, [authLoading, isAuthenticated, navigate, year]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const flags = await getPassportFlags();
        if (!isPassportEnabled(flags)) {
          if (!cancelled) setEnabled(false);
          return;
        }
        if (!cancelled) setEnabled(true);
        const data = (await getPassportRecap(year)) as Recap;
        if (!cancelled) {
          setRecap(data);
          void apiService.trackEvent('passport_recap_viewed', { metadata: { year } });
        }
      } catch (err: unknown) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load recap');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [year]);

  const share = async () => {
    const title = recap?.share?.export_title || `My ${year} Bilt Passport`;
    const text = recap?.tone?.headline || title;
    const url = `${window.location.origin}/passport/recap?year=${year}`;
    try {
      if (navigator.share) {
        await navigator.share({ title, text, url });
      } else {
        await navigator.clipboard.writeText(`${text}\n${url}`);
      }
    } catch {
      // user cancelled share
    }
  };

  if (authLoading || loading) return <PageLoader />;
  if (!enabled) {
    return (
      <PageShell>
        <EmptyState title={PASSPORT_DISABLED_TITLE} description={PASSPORT_DISABLED_DESCRIPTION} />
      </PageShell>
    );
  }
  if (error || !recap) {
    return (
      <PageShell>
        <PageBackLink label="Back to Passport" to="/passport" className="mb-4" />
        <EmptyState title="Recap unavailable" description={error || 'No encounters for this year yet.'} />
      </PageShell>
    );
  }

  const summary = recap.summary || {};
  const favorites = recap.favorites || {};

  return (
    <PageShell>
      <PageBackLink label="Back to Passport" to="/passport" className="mb-4" />
      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-zinc-950 via-zinc-900 to-orange-950/40 px-6 py-10 sm:px-10">
        <div
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 20%, rgba(249,115,22,0.35), transparent 40%), radial-gradient(circle at 80% 0%, rgba(255,255,255,0.08), transparent 35%)',
          }}
          aria-hidden
        />
        <p className="relative text-xs uppercase tracking-[0.2em] text-orange-300/90">{year} Passport</p>
        <h1 className="relative mt-3 max-w-2xl font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">
          {recap.tone?.headline}
        </h1>
        <p className="relative mt-3 max-w-xl text-sm text-zinc-300 sm:text-base">{recap.tone?.subhead}</p>
        <div className="relative mt-6 flex flex-wrap gap-3">
          <Button onClick={() => void share()}>Share year</Button>
          <Button variant="secondary" onClick={() => window.print()}>
            Export / print
          </Button>
        </div>
      </div>

      <PageHeader
        className="mt-10"
        eyebrow="The year in presence"
        title="What you actually saw"
        description="Counts reflect verified Passport encounters — not page views or game points."
      />

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[
          ['Builds seen', summary.vehicles_seen],
          ['Events', summary.events_attended],
          ['Shops', summary.shops_visited],
          ['Clubs', summary.clubs_met],
          ['Collections completed', summary.collections_completed],
          ['Total encounters', summary.total_encounters],
        ].map(([label, value]) => (
          <div
            key={String(label)}
            className="rounded-xl border border-white/10 bg-white/[0.03] px-5 py-4"
          >
            <p className="text-xs uppercase tracking-wide text-zinc-400">{label}</p>
            <p className="mt-2 font-display text-3xl text-white">{value ?? 0}</p>
          </div>
        ))}
      </div>

      <section className="mt-10" aria-labelledby="favorites-heading">
        <h2 id="favorites-heading" className="font-display text-xl text-white">
          Favorites
        </h2>
        <ul className="mt-4 grid gap-3 sm:grid-cols-2">
          {(
            [
              ['Manufacturer', favorites.manufacturer],
              ['Chassis', favorites.chassis],
              ['Drivetrain', favorites.drivetrain],
              ['Engine', favorites.engine],
              ['Event', favorites.event],
              ['Club', favorites.club],
            ] as Array<[string, { label?: string; count?: number } | null | undefined]>
          ).map(([label, item]) => (
            <li key={label} className="rounded-xl border border-white/10 px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-zinc-500">{label}</p>
              <p className="mt-1 text-white">
                {item?.label ? (
                  <>
                    {item.label}
                    {item.count ? <span className="text-zinc-400"> · {item.count}</span> : null}
                  </>
                ) : (
                  <span className="text-zinc-500">Not enough data yet</span>
                )}
              </p>
            </li>
          ))}
        </ul>
      </section>

      {(recap.memorable_build || recap.rarest_encounter) && (
        <section className="mt-10 grid gap-4 md:grid-cols-2">
          {recap.memorable_build ? (
            <div className="rounded-xl border border-orange-500/20 bg-orange-500/5 px-5 py-4">
              <p className="text-xs uppercase tracking-wide text-orange-300/80">Most memorable</p>
              <p className="mt-2 text-lg text-white">{recap.memorable_build.title}</p>
            </div>
          ) : null}
          {recap.rarest_encounter ? (
            <div className="rounded-xl border border-white/10 px-5 py-4">
              <p className="text-xs uppercase tracking-wide text-zinc-500">Highest verification</p>
              <p className="mt-2 text-lg text-white">{recap.rarest_encounter.title}</p>
              <p className="mt-1 text-sm text-zinc-400">{recap.rarest_encounter.verification_level}</p>
            </div>
          ) : null}
        </section>
      )}

      {recap.highlights && recap.highlights.length > 0 ? (
        <section className="mt-10">
          <h2 className="font-display text-xl text-white">Timeline highlights</h2>
          <ol className="mt-4 space-y-3">
            {recap.highlights.map((h) => (
              <li key={h.public_id}>
                <Link
                  to={`/passport/encounters/${h.public_id}`}
                  className="block rounded-xl border border-white/10 px-4 py-3 transition hover:border-orange-500/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
                >
                  <p className="text-white">{h.title}</p>
                  <p className="text-sm text-zinc-400">
                    {h.verification_level}
                    {h.occurred_at ? ` · ${new Date(h.occurred_at).toLocaleDateString()}` : ''}
                  </p>
                </Link>
              </li>
            ))}
          </ol>
        </section>
      ) : null}

      {recap.badges && recap.badges.length > 0 ? (
        <section className="mt-10 mb-8">
          <h2 className="font-display text-xl text-white">Recognition earned</h2>
          <ul className="mt-4 flex flex-wrap gap-2">
            {recap.badges.map((b, i) => (
              <li
                key={b.public_id || String(i)}
                className="rounded-full border border-white/15 bg-white/[0.04] px-3 py-1.5 text-sm text-zinc-200"
              >
                {b.name}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </PageShell>
  );
};

export default PassportRecapPage;
